import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { useState } from 'react'
import {
  InventoryTrackingMode,
  ProcurementPurchaseOrderStatus,
  ReceiveProcurementPurchaseOrderItemRequest,
  ReceiveProcurementPurchaseOrderItemRequestItem,
  ReceiveProcurementPurchaseOrderItemRequestSerial,
} from 'types/procurement'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Spinner } from 'components/Loading/Loading'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { receiveProcurementPurchaseOrderItem } from 'api'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import useModalClose from 'hooks/useModalClose'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import {
  IReceiveItemModalProps,
  IReceiveItemModalValues,
} from '../../../../types/receiving.interface'
import { Button, CursorPointer, Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'

export const ReceiveItemModal = ({
  onClose,
  item,
  order,
  setActiveOrder,
}: IReceiveItemModalProps) => {
  const queryClient = useQueryClient()
  const modalRef = useModalClose(onClose)
  useFocusFirstInput()
  const [values, setValues] = useState<IReceiveItemModalValues>({
    expiryValue: '',
    batchCode: '',
    quantity:
      item.procurementPurchaseRequestItem.inventoryItem.trackingMode ===
        InventoryTrackingMode.Serial
        ? 1
        : '',
    mfgDate: null,
    location: null,
    serialNumber: '',
    warrantyExpiry: null,
  })

  const mutation = useMutation({
    mutationFn: async () => {
      try {
        if (!values.location?.value) {
          toast.error('Location is required')
          return null
        }
        if (!values.quantity || Number(values.quantity) <= 0) {
          toast.error('Quantity is required')
          return null
        }

        const itemData: ReceiveProcurementPurchaseOrderItemRequestItem = {
          procurementPurchaseOrderItemId: item.id,
          quantity: Number(values.quantity),
        }
        if (
          item.procurementPurchaseRequestItem.inventoryItem.trackingMode ===
          InventoryTrackingMode.Serial
        ) {
          if (!values.serialNumber) {
            toast.error('Serial Number is required')
            return null
          }
          const sirialNo: ReceiveProcurementPurchaseOrderItemRequestSerial = {
            serialNo: values.serialNumber,
          }
          if (values.warrantyExpiry) {
            sirialNo.warrantyExpiry = values.warrantyExpiry
          }

          itemData.serials = [sirialNo]
        }
        if (values.expiryValue) {
          itemData.expiryValue = Number(values.expiryValue)
        }
        if (values.batchCode) {
          itemData.batchCode = values.batchCode
        }
        if (values.mfgDate) {
          itemData.mfgDate = values.mfgDate
        }
        const remaining =
          (item.procurementPurchaseRequestItem?.quantity || 0) - (item.receivedQuantity || 0)
        const receiveQty = Number(values.quantity) || 0
        const willCompleteOrder =
          order.items.every((line) => {
            if (line.id === item.id) {
              return remaining - receiveQty <= 0
            }
            return (
              (line.procurementPurchaseRequestItem?.quantity || 0) - (line.receivedQuantity || 0) <=
              0
            )
          })
        const data: ReceiveProcurementPurchaseOrderItemRequest = {
          locationId: Number(values.location?.value),
          status: willCompleteOrder
            ? ProcurementPurchaseOrderStatus.Received
            : ProcurementPurchaseOrderStatus.PartiallyReceived,
          items: [itemData],
        }
        const response = await receiveProcurementPurchaseOrderItem(order.id, data)
        queryClient.invalidateQueries({ queryKey: ['procurement-purchase-orders-receiving'] })
        setActiveOrder(response)
        onClose()
        toast.success(
          willCompleteOrder ? 'Purchase order fully received' : 'Item marked as partially received',
        )
      } catch (error) {
        if (error instanceof AxiosError) {
          const msg = error.response?.data?.message
          toast.error(Array.isArray(msg) ? msg.join('\n') : msg || 'Failed to receive item')
        } else {
          toast.error((error as Error)?.message || 'Failed to receive item')
        }
        throw error
      }
    },
  })

  return (
    <>
      <Modal black ref={modalRef}>
        <Flex column between center>
          <Flex column gap='1rem' width='100%'>
            <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
              <TitleText weight='bold'>Receive Item</TitleText>
              <CursorPointer onClick={onClose}>
                <CloseIcon />
              </CursorPointer>
            </Flex>
            <HonestSelectAsyncInput
              nomarg
              width='100%'
              link='locations'
              placeholder='Search locations'
              name='location'
              labelText='Location'
              defaultValue={values.location?.label}
              onChange={(value) => setValues({ ...values, location: value })}
              parserFunction={parseLocationRegistryOptions}
              invalid={!values.location?.value}
              required
            />
            <Input
              labelText='Expiry Value'
              name='expiryValue'
              placeholder='30'
              type='number'
              value={values.expiryValue}
              onChange={(e) => setValues({ ...values, expiryValue: e.target.value })}
              nomarg
            />
            <Input
              labelText='Batch Code'
              name='batchCode'
              placeholder='C1443'
              value={values.batchCode}
              onChange={(e) => setValues({ ...values, batchCode: e.target.value })}
              nomarg
            />
            {item.procurementPurchaseRequestItem.inventoryItem.trackingMode !==
              InventoryTrackingMode.Serial && (
                <Input
                  labelText='Quantity'
                  name='quantity'
                  placeholder='100'
                  type='number'
                  value={values.quantity}
                  onChange={(e) => setValues({ ...values, quantity: e.target.value })}
                  nomarg
                  required
                />
              )}
            <CalendarInput
              width='100%'
              placeholder='MFG Date'
              labelText='MFG Date'
              nomarg
              defaultValue={values.mfgDate}
              onChange={(date) => {
                setValues({ ...values, mfgDate: date?.toISOString() })
              }}
            />
            {item.procurementPurchaseRequestItem.inventoryItem.trackingMode ===
              InventoryTrackingMode.Serial && (
                <>
                  <Input
                    labelText='Serial Number'
                    name='serialNumber'
                    placeholder='1234567890'
                    value={values.serialNumber}
                    onChange={(e) => setValues({ ...values, serialNumber: e.target.value })}
                    nomarg
                    required
                  />
                  <CalendarInput
                    width='100%'
                    placeholder='Warranty Expiry'
                    labelText='Warranty Expiry'
                    nomarg
                    defaultValue={values.warrantyExpiry}
                    onChange={(date) => {
                      setValues({ ...values, warrantyExpiry: date?.toISOString() })
                    }}
                  />
                </>
              )}
          </Flex>
          <Button mt='16' orange onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Continue
          </Button>
        </Flex>
      </Modal>
      {mutation.isPending && <Spinner loading={mutation.isPending} />}
    </>
  )
}
